import React, { Component } from 'react';
import CalendarDays from '../CalendarDays/CalendarDays';
import CalendarKey from "../Calendar/CalendarKey";
import { Blocks } from "../Common/Interfaces";
import {parseDateFromYYYYMMDD} from "../Common/Helpers";

interface Day {
    year: number;
    month: number;
    number: number;
}

interface CalendarProps {
    onDayClick: (day: Date) => void;
    workingDays: number[];
    blocks: Blocks[];
    nonWorkingDays: string[];
}

interface CalendarState {
    currentDay: Date;
}

export default class Calendar extends Component<CalendarProps, CalendarState> {
    weekdays: string[];
    months: string[];

    constructor(props: CalendarProps) {
        super(props);

        this.weekdays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
        this.months = ['January', 'February', 'March', 'April', 'May', 'June',
            'July', 'August', 'September', 'October', 'November', 'December'];

        this.state = {
            currentDay: new Date(),
        };
    }

    getMinDate = () => {
        const minDate = new Date();
        return minDate;
    }

    getMaxDate = () => {
        const maxDate = new Date();
        maxDate.setFullYear(maxDate.getFullYear() + 1);
        return maxDate;
    }

    changeCurrentDay = (selectedDay: string) => {
        const newDay = parseDateFromYYYYMMDD(selectedDay);
        this.setState({ currentDay: newDay });
        this.props.onDayClick(newDay);
    }

    nextMonth = () => {
        const newDate = new Date(this.state.currentDay);
        newDate.setMonth(newDate.getMonth() + 1);

        // Ensure new date doesn't exceed max date (ignore days, focus on year and month)
        if (newDate.getFullYear() < this.getMaxDate().getFullYear() ||
            (newDate.getFullYear() === this.getMaxDate().getFullYear() && newDate.getMonth() <= this.getMaxDate().getMonth())) {
            this.setState({ currentDay: newDate });
        }
    }

    previousMonth = () => {
        const newDate = new Date(this.state.currentDay);
        newDate.setMonth(newDate.getMonth() - 1);

        // Ensure new date doesn't go below min date (ignore days, focus on year and month)
        if (newDate.getFullYear() > this.getMinDate().getFullYear() ||
            (newDate.getFullYear() === this.getMinDate().getFullYear() && newDate.getMonth() >= this.getMinDate().getMonth())) {
            this.setState({ currentDay: newDate });
        }
    }

    currentMonth = () => {
        const newDate = new Date();
        this.setState({ currentDay: newDate });
    }

    isCurrentMonth = () => {
        const today = new Date();
        const currentViewMonth = this.state.currentDay.getMonth();
        const currentViewYear = this.state.currentDay.getFullYear();

        return today.getMonth() === currentViewMonth && today.getFullYear() === currentViewYear;
    }

    isLastMonth = () => {
        const maxDate = this.getMaxDate();
        const currentViewMonth = this.state.currentDay.getMonth();
        const currentViewYear = this.state.currentDay.getFullYear();

        return currentViewMonth === maxDate.getMonth() && currentViewYear === maxDate.getFullYear();
    }

    isFirstMonth = () => {
        const minDate = this.getMinDate();
        const currentViewMonth = this.state.currentDay.getMonth();
        const currentViewYear = this.state.currentDay.getFullYear();

        return currentViewMonth === minDate.getMonth() && currentViewYear === minDate.getFullYear();
    }

    render() {
        return (
            <div className="calendar">
                <div className="calendar-header">
                    <button
                        className="calendar-button calendar-button--prev"
                        onClick={this.previousMonth}
                        disabled={this.isCurrentMonth()}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                            <path
                                d="M7,12L12,7V10H16V14H12V17L7,12M22,12A10,10 0 0,1 12,22A10,10 0 0,1 2,12A10,10 0 0,1 12,2A10,10 0 0,1 22,12M20,12A8,8 0 0,0 12,4A8,8 0 0,0 4,12A8,8 0 0,0 12,20A8,8 0 0,0 20,12Z"/>
                        </svg>
                    </button>
                    <h3 className="calendar-month-heading">{this.months[this.state.currentDay.getMonth()]} {this.state.currentDay.getFullYear()}</h3>
                    <button
                        className="calendar-button calendar-button--next"
                        onClick={this.nextMonth}
                        disabled={this.isLastMonth()}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                            <path
                                d="M7,12L12,7V10H16V14H12V17L7,12M22,12A10,10 0 0,1 12,22A10,10 0 0,1 2,12A10,10 0 0,1 12,2A10,10 0 0,1 22,12M20,12A8,8 0 0,0 12,4A8,8 0 0,0 4,12A8,8 0 0,0 12,20A8,8 0 0,0 20,12Z"/>
                        </svg>
                    </button>
                </div>
                <button
                    onClick={this.currentMonth}
                    className={
                        'btn-primary btn-primary--light btn-primary--back' +
                        (this.isCurrentMonth() ? " current-month" : "")
                    }
                >
                    Go to current month
                </button>
                <div className="calendar-body">
                    <div className="calendar-days">
                        {this.weekdays.map((weekday, index) => (
                            <div key={index} className="weekday"><p>{weekday}</p></div>
                        ))}
                    </div>
                    <CalendarDays
                        day={this.state.currentDay}
                        changeCurrentDay={this.changeCurrentDay}
                        workingDays={this.props.workingDays}
                        blocks={this.props.blocks}
                        nonWorkingDays={this.props.nonWorkingDays}
                    />
                </div>
                <CalendarKey/>
            </div>
        );
    }
}
